import React, { createContext, useContext, useState } from 'react';

type RovoWidgetConfig = {
	agentId: string;
	/**
	 * ARI of the widget container the agent belongs to, used as the SDK's `widgetId`. Optional
	 * because `RovoWidgetProvider` (the query-backed path) has no container to offer; consumers
	 * must fall back to the help center ARI when it is absent.
	 */
	widgetContainerAri?: string;
};

type RovoWidgetConfigContextType = {
	rovoWidgetConfig: RovoWidgetConfig;
};

const RovoWidgetConfigContext = createContext<RovoWidgetConfigContextType | null>(null);

/**
 * Provides the `RovoWidgetConfigContext` value to the children
 * Value is stored on initial render, so if you change the value, updating the props will not update the context value
 */
export const RovoWidgetConfigProvider = ({
	rovoWidgetConfig,
	children,
}: {
	children: React.ReactNode;
	rovoWidgetConfig: RovoWidgetConfigContextType['rovoWidgetConfig'];
}): React.JSX.Element => {
	const [value] = useState<RovoWidgetConfigContextType>({
		rovoWidgetConfig,
	});

	return (
		<RovoWidgetConfigContext.Provider value={value}>{children}</RovoWidgetConfigContext.Provider>
	);
};

/**
 * Provides the `RovoWidgetConfigContext` value if it exists, otherwise return null
 * Use this hook if you want to access the `RovoWidgetConfigContext` value in a component
 * but have a fallback behaviour if its missing
 */
export const useOptionalRovoWidgetConfig = (): RovoWidgetConfigContextType | null => {
	return useContext(RovoWidgetConfigContext);
};

/**
 * Provides the `RovoWidgetConfigContext` value if it exists, otherwise throws an error
 * Use this hook if you need to access the `RovoWidgetConfigContext` value in a component
 * and should fail fast if its missing
 */
export const useRovoWidgetConfig = (): RovoWidgetConfigContextType => {
	const contextValue = useContext(RovoWidgetConfigContext);

	if (!contextValue) {
		throw new Error('useRovoWidgetConfig must be used within a RovoWidgetConfigProvider');
	}

	return contextValue;
};
